import { isLookSystem } from "@/utils/look-gate";

export type MotionLevel = "none" | "calm" | "lively" | "custom";
export type RevealEffect = "up" | "fade" | "mask" | "none";
export type CardEffect = "zoom" | "lift" | "none";
export type ClickEffect = "press" | "ripple" | "none";
/** »Billedet flyver med« is the crossfade with a card's photo flying to the page it opens; no level sets it. */
export type PageEffect = "fade" | "flight" | "none";
export type ParallaxEffect = "subtle" | "strong" | "none";
export type BandSpeed = "slow" | "normal" | "fast";

interface MotionEffects {
  reveal: RevealEffect;
  stagger: boolean;
  card: CardEffect;
  click: ClickEffect;
  countUp: boolean;
  /** Soft photo loading: a photo below the fold that is still loading fades in once it has loaded. */
  photo: boolean;
  band: boolean;
  page: PageEffect;
  /** Big headlines (h1, h2) rise line by line, never letter by letter. */
  lines: boolean;
  /** A photo moves inside its frame as the frame passes through the view. */
  parallax: ParallaxEffect;
  /** A section's background photo zooms in once, slowly. */
  kenBurns: boolean;
  /** The first section's background photo grows as it scrolls away. */
  grow: boolean;
  /** A label that announces something new (Nyhed, Ny pris, Åbent hus) pulses three times as it comes into view. */
  labelPulse: boolean;
}

/** Milliseconds. */
export interface MotionDurations {
  reveal: number;
  stagger: number;
  zoom: number;
  nudge: number;
  lift: number;
  press: number;
  countUp: number;
  photo: number;
  band: number;
  page: number;
  mask: number;
  lines: number;
  lineStep: number;
  kenBurns: number;
  /** All three pulses together. */
  labelPulse: number;
}

export interface MotionSettings extends MotionEffects {
  /** The level whose preset the effects match, or "custom" (»Tilpasset«) when they match none. */
  level: MotionLevel;
  tempo: number;
  distance: string;
  bandSpeed: BandSpeed;
  /** After tempo (the band after its speed); an exact value from Avanceret is kept as it is. */
  durations: MotionDurations;
  zoomScale: number;
  pressScale: number;
  lift: string;
  nudge: string;
  kenBurnsScale: number;
  /** How far a framed photo moves each way, as a fraction of its height. */
  parallaxShift: number;
  growScale: number;
}

/** A section's or an element's own `motion` choices, stored like `styleSystem.motion`. */
export interface MotionScope {
  motion?: unknown;
  first?: boolean;
  /**
   * On a section: the own motion of the Flows and Grids around an element, outermost first, applied after the
   * section's. They draw no scope of their own, so scopes nest at most two deep (src/styles/motion.css).
   */
  within?: unknown[];
}

interface MotionChoices {
  effects: MotionEffects;
  tempo: number;
  distance: string;
  bandSpeed: BandSpeed;
  exact: Record<string, unknown>;
}

type PresetLevel = Exclude<MotionLevel, "custom">;

const PRESETS: Record<PresetLevel, MotionEffects> = {
  none: {
    reveal: "none",
    stagger: false,
    card: "none",
    click: "none",
    countUp: false,
    photo: false,
    band: false,
    page: "none",
    lines: false,
    parallax: "none",
    kenBurns: false,
    grow: false,
    labelPulse: false,
  },
  calm: {
    reveal: "up",
    stagger: true,
    card: "zoom",
    click: "press",
    countUp: true,
    photo: true,
    band: false,
    page: "fade",
    lines: false,
    parallax: "none",
    kenBurns: false,
    grow: false,
    labelPulse: false,
  },
  lively: {
    reveal: "mask",
    stagger: true,
    card: "zoom",
    click: "press",
    countUp: true,
    photo: true,
    band: true,
    page: "fade",
    lines: true,
    parallax: "subtle",
    kenBurns: true,
    grow: true,
    labelPulse: false,
  },
};

const LEVELS: readonly MotionLevel[] = ["none", "calm", "lively", "custom"];
const CHOICES = {
  reveal: ["up", "fade", "mask", "none"],
  card: ["zoom", "lift", "none"],
  click: ["press", "ripple", "none"],
  page: ["fade", "flight", "none"],
  parallax: ["subtle", "strong", "none"],
} as const;
const FLAGS = [
  "stagger",
  "countUp",
  "photo",
  "band",
  "lines",
  "kenBurns",
  "grow",
  "labelPulse",
] as const;

const DURATIONS: MotionDurations = {
  reveal: 400,
  stagger: 60,
  zoom: 600,
  nudge: 300,
  lift: 300,
  press: 100,
  countUp: 1400,
  photo: 300,
  band: 28000,
  page: 250,
  mask: 700,
  lines: 700,
  lineStep: 110,
  kenBurns: 5000,
  labelPulse: 3000,
};
// WCAG 2.2.2: a label that moves on its own stands still again within five seconds.
const LABEL_PULSE_MAX = 5000;
const BAND_SPEEDS: Record<BandSpeed, number> = {
  slow: 1.8,
  normal: 1,
  fast: 0.55,
};

export const STAGGER_MAX = 6;

const DEFAULTS: MotionChoices = {
  effects: PRESETS.calm,
  tempo: 1,
  distance: "16px",
  bandSpeed: "normal",
  exact: {},
};
const ZOOM_SCALE = 1.04;
const PRESS_SCALE = 0.97;
const LIFT = "3px";
const NUDGE = "4px";
const KEN_BURNS_SCALE = 1.06;
const PARALLAX_PERCENT: Record<Exclude<ParallaxEffect, "none">, number> = {
  subtle: 6,
  strong: 12,
};
const GROW_SCALE = 1.15;

// A scroll-driven reveal runs over scroll distance, not time: 120 px of scroll for the 400 ms reveal.
const SCROLL_PX_PER_MS = 0.3;

const PX = /^\d{1,2}(?:\.\d{1,2})?px$/;

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const oneOf = <T extends string>(
  values: readonly T[],
  value: unknown,
): T | undefined => (values.includes(value as T) ? (value as T) : undefined);

const numberIn = (value: unknown, min: number, max: number) =>
  typeof value === "number" && value >= min && value <= max ? value : undefined;

const pxUpTo = (value: unknown, max: number) =>
  typeof value === "string" && PX.test(value) && parseFloat(value) <= max
    ? value
    : undefined;

function startingEffects(
  level: MotionLevel | undefined,
  inherited: MotionEffects,
  website: boolean,
): MotionEffects {
  if (level && level !== "custom") return PRESETS[level];
  return level === "custom" && website ? PRESETS.none : inherited;
}

function apply(
  base: MotionChoices,
  value: unknown,
  website = false,
): MotionChoices {
  const motion = record(value);
  const level = oneOf(LEVELS, motion.level);
  const effects = { ...startingEffects(level, base.effects, website) };
  for (const key of Object.keys(CHOICES) as (keyof typeof CHOICES)[]) {
    const choice = oneOf<string>(CHOICES[key], motion[key]);
    if (choice) Object.assign(effects, { [key]: choice });
  }
  for (const key of FLAGS)
    if (typeof motion[key] === "boolean") effects[key] = motion[key];
  return {
    effects,
    tempo: numberIn(motion.tempo, 0.25, 4) ?? base.tempo,
    distance: pxUpTo(motion.distance, 64) ?? base.distance,
    bandSpeed:
      oneOf(Object.keys(BAND_SPEEDS) as BandSpeed[], motion.bandSpeed) ??
      base.bandSpeed,
    exact: { ...base.exact, ...record(motion.exact) },
  };
}

function levelOf(effects: MotionEffects): MotionLevel {
  const matches = (preset: MotionEffects) =>
    (Object.keys(preset) as (keyof MotionEffects)[]).every(
      (key) => preset[key] === effects[key],
    );
  return (
    (Object.keys(PRESETS) as PresetLevel[]).find((level) =>
      matches(PRESETS[level]),
    ) ?? "custom"
  );
}

function durations({ tempo, bandSpeed, exact }: MotionChoices) {
  const resolved = { ...DURATIONS };
  for (const key of Object.keys(DURATIONS) as (keyof MotionDurations)[]) {
    const own =
      key === "band"
        ? numberIn(exact.band, 5000, 600000)
        : numberIn(
            exact[key],
            0,
            key === "labelPulse" ? LABEL_PULSE_MAX : 10000,
          );
    const scaled = Math.round(
      DURATIONS[key] * (key === "band" ? BAND_SPEEDS[bandSpeed] : tempo),
    );
    resolved[key] =
      own ??
      (key === "labelPulse" ? Math.min(LABEL_PULSE_MAX, scaled) : scaled);
  }
  return resolved;
}

/**
 * The motion a section or element shows: the website's `styleSystem.motion`, then the section's, then the Flows' and
 * Grids' around the element (`section.within`), then the element's. A preset level resets every effect to that level's;
 * »Tilpasset« on the website starts from no effects, so a converted website shows only the effects it lists. The first
 * section never reveals, its photo, likely the LCP, never waits to fade in, and its headline never moves; only its photo
 * grows.
 */
export function resolveMotion(
  styleSystem: unknown,
  section?: MotionScope,
  element?: MotionScope,
): MotionSettings {
  let choices = apply(DEFAULTS, record(styleSystem).motion, true);
  if (section)
    for (const motion of [section.motion, ...(section.within ?? [])])
      choices = apply(choices, motion);
  if (element) choices = apply(choices, element.motion);
  const { effects, tempo, distance, bandSpeed, exact } = choices;
  return {
    level: levelOf(effects),
    tempo,
    distance,
    bandSpeed,
    ...effects,
    ...(section?.first
      ? { reveal: "none" as const, stagger: false, photo: false, lines: false }
      : {}),
    ...(section && !section.first ? { grow: false } : {}),
    durations: durations(choices),
    zoomScale: numberIn(exact.zoomScale, 1, 1.5) ?? ZOOM_SCALE,
    pressScale: numberIn(exact.pressScale, 0.8, 1) ?? PRESS_SCALE,
    lift: pxUpTo(exact.liftDistance, 16) ?? LIFT,
    nudge: pxUpTo(exact.nudgeDistance, 16) ?? NUDGE,
    kenBurnsScale: numberIn(exact.kenBurnsScale, 1, 1.5) ?? KEN_BURNS_SCALE,
    parallaxShift:
      effects.parallax === "none"
        ? 0
        : (numberIn(exact.parallaxShift, 1, 20) ??
            PARALLAX_PERCENT[effects.parallax]) / 100,
    growScale: numberIn(exact.growScale, 1, 1.5) ?? GROW_SCALE,
  };
}

/** resolveMotion behind the gate: undefined for a website not on the look system. */
export function lookMotion(
  theme: unknown,
  section?: MotionScope,
  element?: MotionScope,
): MotionSettings | undefined {
  return isLookSystem(theme)
    ? resolveMotion(theme.styleSystem, section, element)
    : undefined;
}

/** The `data-re-motion-*` attributes of a motion scope (src/styles/motion.css): one per effect that is on. */
export function motionAttributes(
  settings: MotionSettings | undefined,
): Record<string, string> {
  if (!settings) return {};
  const attributes: Record<string, string> = {
    "data-re-motion": settings.level,
  };
  if (settings.reveal !== "none") {
    attributes["data-re-motion-reveal"] = settings.reveal;
    if (settings.stagger) attributes["data-re-motion-stagger"] = "";
  }
  if (settings.card !== "none")
    attributes["data-re-motion-card"] = settings.card;
  if (settings.click !== "none")
    attributes["data-re-motion-click"] = settings.click;
  if (settings.countUp) attributes["data-re-motion-count"] = "";
  if (settings.photo) attributes["data-re-motion-photo"] = "";
  if (settings.band) attributes["data-re-motion-band"] = "";
  if (settings.lines) attributes["data-re-motion-lines"] = "";
  if (settings.parallax !== "none")
    attributes["data-re-motion-parallax"] = settings.parallax;
  if (settings.kenBurns) attributes["data-re-motion-kenburns"] = "";
  if (settings.grow) attributes["data-re-motion-grow"] = "";
  if (settings.labelPulse) attributes["data-re-motion-label-pulse"] = "";
  return attributes;
}

/** The values motion.css reads, inherited from the nearest element that declares them; a masked reveal takes its own time. */
export function motionVars(settings: MotionSettings): Record<string, string> {
  const { durations } = settings;
  const ms = (value: number) => `${value}ms`;
  const scroll = (value: number) => `${Math.round(value * SCROLL_PX_PER_MS)}px`;
  const reveal = settings.reveal === "mask" ? durations.mask : durations.reveal;
  return {
    "--re-reveal-duration": ms(reveal),
    "--re-reveal-distance": settings.distance,
    "--re-reveal-range": scroll(reveal),
    "--re-stagger-step": ms(durations.stagger),
    "--re-stagger-range": scroll(durations.stagger),
    "--re-zoom-scale": String(settings.zoomScale),
    "--re-zoom-duration": ms(durations.zoom),
    "--re-nudge": settings.nudge,
    "--re-nudge-duration": ms(durations.nudge),
    "--re-lift": settings.lift,
    "--re-lift-duration": ms(durations.lift),
    "--re-press-scale": String(settings.pressScale),
    "--re-press-duration": ms(durations.press),
    "--re-count-duration": ms(durations.countUp),
    "--re-photo-duration": ms(durations.photo),
    "--re-band-duration": ms(durations.band),
    "--re-page-duration": ms(durations.page),
    "--re-lines-duration": ms(durations.lines),
    "--re-lines-step": ms(durations.lineStep),
    "--re-kenburns-duration": ms(durations.kenBurns),
    "--re-kenburns-scale": String(settings.kenBurnsScale),
    "--re-parallax": String(settings.parallaxShift),
    "--re-grow-scale": String(settings.growScale),
    "--re-label-pulse-duration": ms(durations.labelPulse),
  };
}

/** What a motion scope's element carries: its effects, and the motionVars that differ from the scope around it. */
export interface MotionScopeProps {
  attributes: Record<string, string>;
  style: Record<string, string>;
}

function scopeProps(
  settings: MotionSettings,
  around: MotionSettings,
): MotionScopeProps {
  const outer = motionVars(around);
  return {
    attributes: motionAttributes(settings),
    style: Object.fromEntries(
      Object.entries(motionVars(settings)).filter(
        ([name, value]) => outer[name] !== value,
      ),
    ),
  };
}

/** A section wrapper's scope from the section's own `ui.motion`; undefined for a website not on the look system. */
export function sectionMotion(
  theme: unknown,
  section: unknown,
  first: boolean,
): MotionScopeProps | undefined {
  if (!isLookSystem(theme)) return undefined;
  const motion = record(record(section).ui).motion;
  return scopeProps(
    resolveMotion(theme.styleSystem, { motion, first }),
    resolveMotion(theme.styleSystem),
  );
}

const ownMotion = (element: unknown) => {
  const motion = record(record(element).config).motion;
  return Object.keys(record(motion)).length ? motion : undefined;
};

/**
 * An element's scope from its own `config.motion` and the Flows' and Grids' around it, inside `section`; undefined
 * outside a section and where neither has its own motion, so the element follows its section's scope. Its values are
 * those that differ from the section's, the scope around it in the page.
 */
export function elementMotion(
  theme: unknown,
  section: MotionScope | undefined,
  element: unknown,
): MotionScopeProps | undefined {
  const own = ownMotion(element);
  if (!isLookSystem(theme) || !section || (!own && !section.within?.length))
    return undefined;
  return scopeProps(
    resolveMotion(
      theme.styleSystem,
      section,
      own ? { motion: own } : undefined,
    ),
    resolveMotion(theme.styleSystem, { motion: section.motion }),
  );
}

/** `section` as the elements in a Flow or a Grid see it: the container's own motion applies to every element in it. */
export function innerMotion(
  section: MotionScope,
  container: unknown,
): MotionScope {
  const own = ownMotion(container);
  return own
    ? { ...section, within: [...(section.within ?? []), own] }
    : section;
}

const CONTAINERS = new Set(["FlowComponent", "GridComponent"]);

/**
 * What the box an element sits in carries in a section whose elements reveal one by one (a dynamic or a stacked
 * section): a reveal marker, staggered by `index` among the elements beside it, that is also the element's scope where
 * elementMotion() gives one. Undefined outside such a section and for a Flow or a Grid, whose elements are the units;
 * what an element holds, such as a slider's slides, moves with it.
 */
export function elementUnit(
  theme: unknown,
  section: MotionScope | undefined,
  element: unknown,
  index: number,
): MotionScopeProps | undefined {
  if (
    !isLookSystem(theme) ||
    !section ||
    CONTAINERS.has(String(record(element).type))
  )
    return undefined;
  const scope = elementMotion(theme, section, element);
  return {
    attributes: { "data-re-reveal": "", ...scope?.attributes },
    style: { "--re-reveal-index": String(index), ...scope?.style },
  };
}

const reducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const nearestScope = (element: Element | null | undefined) =>
  element?.closest("[data-re-motion]") ?? null;

/**
 * On the look system, whether `element` must hold still: the visitor prefers reduced motion, or its nearest motion
 * scope is at Ingen. Always false on an old website, which keeps today's motion.
 */
export function motionStill(element: Element | null | undefined): boolean {
  if (!element?.closest("html[data-re-colors]")) return false;
  return (
    reducedMotion() ||
    nearestScope(element)?.getAttribute("data-re-motion") === "none"
  );
}

/** On the look system the ripple is an option, not a default: only where the nearest scope chose it. */
export function rippleAllowed(element: Element | null | undefined): boolean {
  return (
    !reducedMotion() &&
    nearestScope(element)?.getAttribute("data-re-motion-click") === "ripple"
  );
}

/** The website's motion values on the document, or '' for a website not on the look system. */
export function motionCss(theme: unknown): string {
  const settings = lookMotion(theme);
  if (!settings) return "";
  const declarations = Object.entries(motionVars(settings)).map(
    ([name, value]) => `${name}: ${value};`,
  );
  return `html[data-re-colors] { ${declarations.join(" ")} }`;
}

/**
 * The `marker`s inside a `scope` (a selector for one effect's attribute) that it is the nearest motion scope of: one in
 * an element scope without the effect does not follow the section's. Scopes nest at most two deep, a section and an
 * element in it.
 */
export const targetsInside = (marker: string, scope: string) =>
  `${scope} ${marker}:not([data-re-motion]):not(${scope} [data-re-motion]:not(${scope}) ${marker})`;

/** targetsInside, and the marker that is such a scope itself; motion.css writes its selectors out the same way. */
export const scopeTargets = (marker: string, scope: string) =>
  `${marker}${scope}, ${targetsInside(marker, scope)}`;

export const REVEAL_TARGETS = scopeTargets(
  "[data-re-reveal]",
  "[data-re-motion-reveal]",
);
export const STAGGER_TARGETS = scopeTargets(
  "[data-re-reveal]",
  "[data-re-motion-stagger]",
);
export const COUNT_TARGETS = scopeTargets(
  "[data-re-count]",
  "[data-re-motion-count]",
);
/** A photo is an `img`. */
export const PHOTO_TARGETS = scopeTargets(
  "[data-re-photo]",
  "[data-re-motion-photo]",
);
export const LINES_TARGETS = targetsInside(
  ":is(h1, h2)",
  "[data-re-motion-lines]",
);
export const SECTION_PHOTO = ":is(.re-layer-bg-photo, .re-background-photo)";
export const KEN_BURNS_TARGETS = targetsInside(
  SECTION_PHOTO,
  "[data-re-motion-kenburns]",
);
/** A photo that is its own frame: while it moves inside it, motion.css clips it to its box. */
export const PARALLAX_TARGETS = scopeTargets(
  "[data-re-frame-photo]",
  "[data-re-motion-parallax]",
);
/** Keeps each framed photo's own corner in --re-frame-radius, which motion.css rounds its clip by. */
export function holdFrameRadii(
  photos: Iterable<Element>,
  win: Pick<Window, "getComputedStyle">,
) {
  for (const photo of photos)
    (photo as HTMLElement).style?.setProperty(
      "--re-frame-radius",
      win.getComputedStyle(photo).borderTopLeftRadius,
    );
}
export const LABEL_PULSE_TARGETS = scopeTargets(
  "[data-re-label-pulse]",
  "[data-re-motion-label-pulse]",
);

const LINE_TOLERANCE = 2;

/** The line, from 0, of each word whose box starts at `tops` in reading order. */
export function lineIndexes(tops: number[]): number[] {
  const lines: number[] = [];
  return tops.map((top) => {
    const line = lines.findIndex(
      (start) => Math.abs(start - top) <= LINE_TOLERANCE,
    );
    if (line >= 0) return line;
    lines.push(top);
    return lines.length - 1;
  });
}

const NUMBER = /\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+(?:,\d+)?/;

/**
 * `text` with its first number at `fraction` of its value, in the number's own Danish format; `text` itself from 1, and
 * undefined when it holds no number.
 */
export function countUpText(
  text: string,
  fraction: number,
): string | undefined {
  const match = NUMBER.exec(text);
  if (!match) return undefined;
  if (fraction >= 1) return text;
  const [whole, decimals = ""] = match[0].split(",");
  const value = Number(`${whole.replace(/\./g, "")}.${decimals || "0"}`);
  const [integer, fractional] = (value * Math.max(0, fraction))
    .toFixed(decimals.length)
    .split(".");
  const grouped = whole.includes(".")
    ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    : integer;
  return `${text.slice(0, match.index)}${grouped}${fractional ? `,${fractional}` : ""}${text.slice(match.index + match[0].length)}`;
}
